import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Chip, Icon} from '@ezcater/tapas';
import {faLocationDot, faUtensils} from '@fortawesome/pro-regular-svg-icons';
import {Form, Formik} from 'formik';
import {useRouter} from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import breakfastAvif from '@/assets/icons/quickFilters/breakfast.avif';
import breakfastPng from '@/assets/icons/quickFilters/breakfast.png';
import breakfastWebp from '@/assets/icons/quickFilters/breakfast.webp';
import glutenFreeAvif from '@/assets/icons/quickFilters/gluten-free.avif';
import glutenFreePng from '@/assets/icons/quickFilters/gluten-free.png';
import glutenFreeWebp from '@/assets/icons/quickFilters/gluten-free.webp';
import groupOrderAvif from '@/assets/icons/quickFilters/group-order.avif';
import groupOrderPng from '@/assets/icons/quickFilters/group-order.png';
import groupOrderWebp from '@/assets/icons/quickFilters/group-order.webp';
import individuallyPackagingAvif from '@/assets/icons/quickFilters/individual-packaging.avif';
import individuallyPackagingPng from '@/assets/icons/quickFilters/individual-packaging.png';
import individuallyPackagingWebp from '@/assets/icons/quickFilters/individual-packaging.webp';
import mexicanAvif from '@/assets/icons/quickFilters/mexican.avif';
import mexicanPng from '@/assets/icons/quickFilters/mexican.png';
import mexicanWebp from '@/assets/icons/quickFilters/mexican.webp';
import sandwichesAvif from '@/assets/icons/quickFilters/sandwiches.avif';
import sandwichesPng from '@/assets/icons/quickFilters/sandwiches.png';
import sandwichesWebp from '@/assets/icons/quickFilters/sandwiches.webp';
import QuickFilterButton, {
  QuickFilterButtonOption,
} from '@/components/features/home/HomeRoot/QuickFilterButton';
import {CuisineTaxonEnum} from '@/components/features/search/filters/FiltersUtils/cuisines';
import AddressSearchAutocomplete from '@/components/global/address/AddressSearchAutocomplete';
import {EXPLORE, POPULAR} from '@/components/global/layout/AppBar/SearchBar/utils';
import {useFulfillmentDetailState} from '@/components/global/providers/FulfillmentDetailStateProvider';
import EppoFlags from '@/EppoFlags';
import {useStringAssignmentExperimentDetails} from '@/eppoHooks';
import {
  DietaryOptionEnum,
  type FulfillmentDetailAddressInput,
  SearchServiceCapabilitiesTaxonomy,
  TypeaheadDocType,
} from '@/graphql/types';
import {useGoogleMapsGeocode} from '@/hooks/useGoogleMapsGeocode';
import useGroupOrderingFeatureCallout from '@/hooks/useGroupOrderingFeatureCallout';
import useMediaQuery from '@/hooks/useMediaQuery';
import usePreciseAddressNudge, {PreciseAddressNudgeVariation} from '@/hooks/usePreciseAddressNudge';
import useScrollDepth from '@/hooks/useScrollDepth';
import useTracking from '@/hooks/useTracking';
import {SEARCH_NEW_PATH} from '@/paths';
import {compilePath} from '@/utils';
import {
  fulfillmentDetailAddressInputToStartNewSearchAddressParams,
  fulfillmentDetailAddressInputToString,
} from '@/utils/geocodedAddressResult';
import {type GeocodedAddressResult} from '@/utils/googleAutocomplete';
import useManualAddressResolver from '../hooks/useManualAddressResolver';
import GeolocationAddressSync from './KeyWordSearch/GeolocationAddressSync';
import KeywordSearchInput, {type KeywordSearchSelection} from './KeyWordSearch/KeyWordSearchInput';
import {resolveKeywordFormAddress} from './resolveKeywordFormAddress';

type StartOrderFormKeywordLocationProps = {
  geoipRecommendedDefaultAddress?: FulfillmentDetailAddressInput | null;
};

type FormValues = {
  address?: FulfillmentDetailAddressInput;
  // The addressString that produced `address` (set by useManualAddressResolver).
  // Lets resolveKeywordFormAddress reuse `address` instead of re-geocoding.
  lastResolvedAddressString: string;
  addressString: string;
  geocodedAddressResult: GeocodedAddressResult | null;
  googlePlace: google.maps.places.AutocompletePrediction | null;
  keyword: string;
  suggestion: KeywordSearchSelection | null;
};

type QuickFilterId =
  | 'breakfast'
  | 'mexican'
  | 'sandwiches'
  | 'individualPackaging'
  | 'glutenFree'
  | 'groupOrdering'
  | 'more';

type QuickFilter = {
  id: QuickFilterId;
  filterParams: QuickFilterButtonOption['filterParams'];
  icon?: {avif: typeof breakfastAvif; webp: typeof breakfastWebp; png: typeof breakfastPng};
  hideOnDesktop?: boolean;
};

const QUICK_FILTERS: QuickFilter[] = [
  {
    id: 'breakfast',
    icon: {avif: breakfastAvif, webp: breakfastWebp, png: breakfastPng},
    filterParams: {cuisineOption: CuisineTaxonEnum.Breakfast},
  },
  {
    id: 'mexican',
    icon: {avif: mexicanAvif, webp: mexicanWebp, png: mexicanPng},
    filterParams: {cuisineOption: CuisineTaxonEnum.Mexican},
  },
  {
    id: 'sandwiches',
    icon: {avif: sandwichesAvif, webp: sandwichesWebp, png: sandwichesPng},
    filterParams: {cuisineOption: CuisineTaxonEnum.Sandwiches},
  },
  {
    id: 'individualPackaging',
    icon: {
      avif: individuallyPackagingAvif,
      webp: individuallyPackagingWebp,
      png: individuallyPackagingPng,
    },
    filterParams: {individualPackaging: true},
  },
  {
    id: 'glutenFree',
    icon: {avif: glutenFreeAvif, webp: glutenFreeWebp, png: glutenFreePng},
    filterParams: {'dietaryOptions[]': [DietaryOptionEnum.GlutenFree]},
  },
  {
    id: 'groupOrdering',
    icon: {avif: groupOrderAvif, webp: groupOrderWebp, png: groupOrderPng},
    filterParams: {serviceCapabilitiesTaxonomy: SearchServiceCapabilitiesTaxonomy.GroupOrdering},
  },
  {id: 'more', hideOnDesktop: true, filterParams: {}},
];

const NewChip = () => {
  const {t} = useTranslation('home');

  return (
    <Chip
      variant="success"
      className="absolute right-2 top-3 rounded-full text-xs font-semibold min-[640px]:static min-[640px]:ml-2 min-[640px]:align-text-top"
    >
      {t('quickFilterButtons.new')}
    </Chip>
  );
};

const StartOrderFormKeywordLocation: React.FC<StartOrderFormKeywordLocationProps> = ({
  geoipRecommendedDefaultAddress,
}) => {
  const {t} = useTranslation('home');
  const router = useRouter();
  const {trackClick} = useTracking();
  const {fallbackAddress: globalFallbackAddress} = useFulfillmentDetailState();

  const fallbackAddress = geoipRecommendedDefaultAddress ?? globalFallbackAddress;
  const {deleteScrollDepth} = useScrollDepth();
  const {fetchGeocodedAddress} = useGoogleMapsGeocode();
  const [isLoading, setIsLoading] = useState(false);
  const [isAddressDropdownOpen, setIsAddressDropdownOpen] = useState(false);
  const rowContainerRef = useRef<HTMLDivElement>(null);
  const isSideBySide = useMediaQuery('(min-width: 924px)');

  const {
    seenRef: preciseAddressNudgeRef,
    showRedDot,
    showTextPrompt,
  } = usePreciseAddressNudge({
    trackExposure: true,
  });
  const showEzx578Experiment = showRedDot || showTextPrompt;

  const {
    dismissCallout: dismissGroupOrderingCallout,
    shouldShowCallout: shouldShowGroupOrderingCallout,
  } = useGroupOrderingFeatureCallout();

  const {variation, trackExperimentExposed} = useStringAssignmentExperimentDetails(
    EppoFlags.GroupOrderingPhase1,
  );
  const shouldShowEzx569GroupOrderingUpdates = variation === 'treatment';

  const fallbackAddressString = fallbackAddress
    ? fulfillmentDetailAddressInputToString(fallbackAddress)
    : '';

  // Keep form address value up-to-date and usable for keyword search typeahead
  // if/when user manually updates address string input.
  const resolveManualAddress = useManualAddressResolver({
    fetchGeocodedAddress,
    fallbackAddress,
    fallbackAddressString,
  });

  useEffect(() => {
    const routeChangeStart = () => setIsLoading(true);
    const routeChangeCompleteOrError = () => setIsLoading(false);
    router.events.on('routeChangeStart', routeChangeStart);
    router.events.on('routeChangeComplete', routeChangeCompleteOrError);
    router.events.on('routeChangeError', routeChangeCompleteOrError);

    return () => {
      router.events.off('routeChangeStart', routeChangeStart);
      router.events.off('routeChangeComplete', routeChangeCompleteOrError);
      router.events.off('routeChangeError', routeChangeCompleteOrError);
    };
  }, [router.events]);

  useEffect(() => {
    if (variation !== 'fallback') {
      trackExperimentExposed();
    }
  }, [variation, trackExperimentExposed]);

  const resolveAddress = (values: FormValues) =>
    resolveKeywordFormAddress(values, {
      fallbackAddress,
      fallbackAddressString,
      fetchGeocodedAddress,
    });

  const navigateToSearch = (
    params: Record<string, unknown>,
    address?: FulfillmentDetailAddressInput,
  ) => {
    const addressParams = fulfillmentDetailAddressInputToStartNewSearchAddressParams(
      address ?? undefined,
    );
    deleteScrollDepth();
    router.push(
      compilePath(
        SEARCH_NEW_PATH,
        {},
        {...addressParams, ...params},
        {skipEmptyString: true, skipNull: true},
      ),
    );
  };

  const navigateForSuggestion = (
    suggestion: KeywordSearchSelection,
    address?: FulfillmentDetailAddressInput,
  ) => {
    if ('filter' in suggestion) {
      navigateToSearch({...suggestion.filter.filters}, address);
      return;
    }
    switch (suggestion.type) {
      case EXPLORE:
        navigateToSearch({...suggestion.additionalParams}, address);
        return;
      case TypeaheadDocType.Cuisine:
        navigateToSearch({cuisineOption: suggestion.id}, address);
        return;
      case TypeaheadDocType.Diversity:
        navigateToSearch({deiOwnedBusinessTaxonomy: suggestion.id}, address);
        return;
      default:
        navigateToSearch({keyword: suggestion.display}, address);
    }
  };

  const getTrackingEvent = (suggestion: KeywordSearchSelection) => {
    if ('filter' in suggestion) return 'home-keyword-suggested-search';
    if (suggestion.category === POPULAR) return 'home-keyword-popular';
    if (suggestion.category === EXPLORE) return 'home-keyword-exploration';
    return 'home-start-order-keyword-suggestion-click';
  };

  const advanceToAddressInput = () => {
    rowContainerRef.current?.querySelector<HTMLInputElement>('[data-address-input] input')?.focus();
    setIsAddressDropdownOpen(true);
  };

  const quickFilterButtonPrefixClassnames = 'w-10 h-10';

  const getButtonLabel = useCallback(
    (id: QuickFilterId) =>
      id === 'groupOrdering' ? (
        <>
          {t(`quickFilterButtons.${id}`)}
          {shouldShowGroupOrderingCallout && <NewChip />}
        </>
      ) : (
        <>{t(`quickFilterButtons.${id}`)}</>
      ),
    [shouldShowGroupOrderingCallout, t],
  );

  const quickFilterButtons: QuickFilterButtonOption[] = useMemo(
    () =>
      QUICK_FILTERS.filter(({id}) =>
        shouldShowEzx569GroupOrderingUpdates ? id !== 'glutenFree' : id !== 'groupOrdering',
      ).map(({id, icon, filterParams, hideOnDesktop}) => ({
        id,
        hideOnDesktop,
        label: getButtonLabel(id),
        // Clicking through to group ordering retires its callouts for good.
        onClick: id === 'groupOrdering' ? dismissGroupOrderingCallout : undefined,
        prefix: icon ? (
          <picture>
            <source srcSet={icon.avif.src} type="image/avif" />
            <source srcSet={icon.webp.src} type="image/webp" />

            {/* Fallback */}
            <img
              alt=""
              className={quickFilterButtonPrefixClassnames}
              decoding="async"
              height={40}
              src={icon.png.src}
              width={40}
            />
          </picture>
        ) : (
          <div
            className={twJoin(
              quickFilterButtonPrefixClassnames,
              'flex items-center justify-center rounded-full bg-green-300',
            )}
          >
            <Icon icon={faUtensils} className="text-green-700" />
          </div>
        ),
        filterParams,
      })),
    [
      quickFilterButtonPrefixClassnames,
      shouldShowEzx569GroupOrderingUpdates,
      getButtonLabel,
      dismissGroupOrderingCallout,
    ],
  );

  return (
    <Formik<FormValues>
      initialValues={{
        address: fallbackAddress ?? undefined,
        lastResolvedAddressString: '',
        addressString: fallbackAddressString,
        geocodedAddressResult: null,
        googlePlace: null,
        keyword: '',
        suggestion: null,
      }}
      onSubmit={async values => {
        trackClick('home-start-order-keyword-location-search-click');
        const address = await resolveAddress(values);
        if (values.suggestion) {
          navigateForSuggestion(values.suggestion, address);
          return;
        }
        navigateToSearch({keyword: values.keyword.trim() || undefined}, address);
      }}
    >
      {({isSubmitting, values, setFieldValue}) => (
        <Form ref={preciseAddressNudgeRef} className="relative" id="start-order-keyword-only">
          <div className="flex flex-col gap-2">
            <GeolocationAddressSync />
            <div
              ref={rowContainerRef}
              className="group flex max-w-[640px] flex-col gap-2 rounded-full max-desktop924:mx-auto max-desktop924:w-full max-desktop924:px-4 desktop924:h-[72px] desktop924:w-[880px] desktop924:max-w-[880px] desktop924:flex-row desktop924:items-stretch desktop924:gap-0 desktop924:bg-white desktop924:pr-3 desktop924:focus-within:bg-[#f4f4f4]"
            >
              <KeywordSearchInput
                dropdownStyle={showEzx578Experiment ? 'experiment' : 'classic'}
                layout="multi"
                onSelect={async suggestion => {
                  trackClick(getTrackingEvent(suggestion));

                  if (!showEzx578Experiment) {
                    const address = await resolveAddress(values);
                    navigateForSuggestion(suggestion, address);
                    return;
                  }

                  void setFieldValue('suggestion', suggestion);
                  advanceToAddressInput();
                }}
                onEnter={showEzx578Experiment ? advanceToAddressInput : undefined}
              />
              <div
                aria-hidden="true"
                className="hidden h-12 w-px shrink-0 self-center bg-peppercorn-100 desktop924:block desktop924:group-focus-within:hidden desktop924:peer-hover:hidden desktop924:group-has-[[data-address]:hover]:hidden"
              />
              <div
                data-address
                className="group/address desktop924:flex desktop924:min-w-0 desktop924:flex-1 desktop924:items-stretch"
              >
                <div
                  data-address-input
                  className="group/address-input min-w-0 flex-1 desktop924:flex desktop924:items-stretch"
                >
                  <AddressSearchAutocomplete
                    experimentVariation={
                      showRedDot
                        ? PreciseAddressNudgeVariation.RedDot
                        : showTextPrompt
                          ? PreciseAddressNudgeVariation.TextPrompt
                          : undefined
                    }
                    id="start-order-keyword-location-address"
                    geocodedAddressResultName="geocodedAddressResult"
                    googlePlaceName="googlePlace"
                    inputName="addressString"
                    savedAddressName="savedAddress"
                    shouldSubmitOnSelect
                    onInputChange={newValue => {
                      if (showEzx578Experiment) return;
                      setIsAddressDropdownOpen(
                        Boolean(newValue) && newValue !== fallbackAddressString,
                      );
                    }}
                    open={isAddressDropdownOpen}
                    onOpenChange={nextOpen => {
                      // In the experiment the dropdown also opens on input click, not only on typing.
                      if (!nextOpen || showEzx578Experiment) setIsAddressDropdownOpen(nextOpen);
                    }}
                    prefix={<Icon icon={faLocationDot} size="small" />}
                    slotProps={{
                      inputGroup: {
                        className:
                          'h-[48px] w-full gap-3 rounded-full border-0 pl-6 focus-within:rounded-full desktop924:h-full desktop924:group-hover/address-input:bg-[#f4f4f4] desktop924:group-hover/address-input:[&_input]:bg-[#f4f4f4] desktop924:focus-within:bg-white desktop924:focus-within:ring-2 desktop924:focus-within:ring-peppercorn-900 desktop924:focus-within:[&_input]:bg-white desktop924:group-hover/address-input:focus-within:bg-white desktop924:group-hover/address-input:focus-within:[&_input]:bg-white desktop924:group-has-[[data-keyword-input]:focus-within]:bg-[#f4f4f4] desktop924:group-has-[[data-keyword-input]:focus-within]:[&_input]:bg-[#f4f4f4]',
                      },
                      input: {
                        'data-testid': 'home-keyword-location-address-input',
                        placeholder: t('addressInputLabel'),
                        // Resolve the entered address when the user leaves the field.
                        onBlur: () => {
                          setIsAddressDropdownOpen(false);
                          void resolveManualAddress(values, setFieldValue);
                        },
                      },
                      content: {
                        anchor: isSideBySide && !showEzx578Experiment ? rowContainerRef : undefined,
                        className: twJoin(
                          'min-w-0',
                          // Sized to its anchor (the address input) by default; widen it on desktop.
                          showEzx578Experiment && 'rounded-2xl desktop924:w-[460px]',
                        ),
                        sideOffset: 9,
                      },
                      item: {
                        className:
                          'rounded-lg px-2 py-1 hover:!bg-peppercorn-50 data-[highlighted]:bg-peppercorn-50',
                      },
                    }}
                  />
                </div>
              </div>
              <Button
                className="h-12 gap-3 px-3 py-4 text-base/[22px] font-bold text-white max-desktop924:bg-[#00e15d] max-desktop924:text-black desktop924:ml-2 desktop924:self-center"
                loading={isSubmitting || isLoading}
                type="submit"
              >
                {t('submitButton')}
              </Button>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-2 max-[639px]:px-4 min-[640px]:mx-4 min-[640px]:mt-8 min-[640px]:flex min-[640px]:flex-wrap min-[640px]:justify-center min-[640px]:gap-3 desktop1248:-mx-20">
              {quickFilterButtons.map(button => (
                <QuickFilterButton
                  addressString={values.addressString}
                  button={button}
                  key={button.id}
                  useNewBreakpoint={true}
                />
              ))}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default StartOrderFormKeywordLocation;
